import { Component, Input, OnInit, DestroyRef, inject } from '@angular/core';
import { AuthorizationCertPeriod, AuthorizationRequest, DocumentsTabInfo, PhysicianNew, ReviewPeriodType, ServiceLocationDTO } from '@core/model';
import { LookupService } from '@core/service';
import { ReviewPeriod, UploadFile } from '@shared/model';
import { PhysicianAccountPgpDTO, PhysicianAccountPgpsDTO } from '@mynexus/mynexus-ui-lib';
import { last } from 'lodash-es';
import moment from 'moment';
import { PRIMARY_ICD_MESSAGE } from '@core/constants/ICD';
import { FeatureToggleService } from '@mynexus/mynexus-ui-lib';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FeatureFlags } from '@core/enumeration';

@Component({
    selector: 'app-auth-review',
    templateUrl: './auth-review.component.html',
    styleUrls: ['./auth-review.component.scss'],
    standalone: false
})
export class AuthReviewComponent implements OnInit {
  @Input() tenantId: string;
  @Input() isExternalUser: boolean;
  @Input() authRequest: AuthorizationRequest;
  @Input() serviceProvider: PhysicianAccountPgpDTO;
  @Input() serviceLocationDTO: ServiceLocationDTO;
  @Input() orderingPhysician: PhysicianAccountPgpsDTO;
  @Input() orderingPhysicianNew: PhysicianNew;
  @Input() documents: UploadFile[];
  @Input() arrangeForAnotherAgency: boolean;
  @Input() documentsTabInfo: DocumentsTabInfo;
  @Input() tenantName: string;
  @Input() isPASCompleteFlow = false;
  private destroyRef = inject(DestroyRef);

  certPeriodRange: string;
  primaryICDisNotPDGM: string;
  route: any;
  showPDGMWarning = true;
  reviewPeriodType = ReviewPeriodType;
  isHH30DayReviewPeriods = false;


  constructor(
    private lookupService: LookupService,
    private readonly featureToggleService: FeatureToggleService
  ) { }

  ngOnInit() {
    this.primaryICDisNotPDGM = this.isExternalUser
      ? PRIMARY_ICD_MESSAGE.EXTERNAL
      : PRIMARY_ICD_MESSAGE.INTERNAL;
    const { effectiveFromDate, effectiveToDate } = last(this.authRequest.authorizationCertPeriodDTOs);
    this.certPeriodRange = `${moment(effectiveFromDate).format('MM/DD/yyyy')} - ${moment(
      effectiveToDate
    ).format('MM/DD/yyyy')}`;
    this.verifyIcdsAttached();
   this.featureToggleService.getFeatureAccess(FeatureFlags.HH30DayReviewPeriods)
   .pipe(takeUntilDestroyed(this.destroyRef))   
   .subscribe((isHH30DayReviewPeriods) => {
        this.isHH30DayReviewPeriods = isHH30DayReviewPeriods;
      });
  }

  ngOnDestroy(): void {
  }

  showCareDetailsInMultiline(): boolean {
    return (
      this.authRequest.authorizationDTO.familyOrFriendsAssistingWithCare &&
      /\r|\n/.test(this.authRequest.authorizationDTO.familyOrFriendsAssistingWithCare)
    );
  }

  get authorizationIcdDTOs() {
    if (this.authRequest.authorizationIcdDTOs[0]?.icdorderType === 'Primary') {
      return this.authRequest.authorizationIcdDTOs;
    } else {
      return [...this.authRequest.authorizationIcdDTOs].reverse();
    }
  }

  verifyIcdsAttached() {
    this.authRequest.authorizationIcdDTOs.forEach(e => {
      if (!e.icdCodeDTO) {
        this.lookupService.getIcdCodeById(this.tenantId, e.icdcodeId).subscribe(resp => {
          if (resp) {
            e.icdCodeDTO = resp;
            if (e.icdorderType === 'Primary') {
              this.showPDGMWarning = !e.icdCodeDTO.pdgm;
            }
          }
        });
      } else {
        if (e.icdorderType === 'Primary') {
          this.showPDGMWarning = !e.icdCodeDTO.pdgm;
        }
      }
    });
  }

  getReviewPeriodDetails(reviewPeriodType: string, certPeriod: number): { type: string, dateRange: string, showIcon: boolean } {
    const reviewPeriod = this.authRequest?.authorizationCertPeriodDTOs
      ?.find(authCertPeriod => authCertPeriod.certPeriod === certPeriod)
      ?.reviewPeriods
      ?.find(period => period.reviewPeriodType === reviewPeriodType);

    if (reviewPeriod) {
      const type = reviewPeriodType === this.reviewPeriodType.ReviewPeriodA ? 'A' : 'B';
      const dateRange = `${moment(reviewPeriod.startDate).format('MM/DD/yyyy')} - ${moment(reviewPeriod.endDate).format('MM/DD/yyyy')}`;
      return { type, dateRange, showIcon: true };
    } else {
      return { type: '', dateRange: '', showIcon: false };
    }
  }
}
